import { cn } from '@/lib/utils';
import React, { useContext, useMemo, useState } from 'react';
import { ConditionalOverlay, SidePanelOverlay } from 'venn-components';
import { assertExhaustive } from 'venn-utils';
import { PortfolioPreview } from './components/PortfolioPreview';
import type { CombinePortfoliosDraftSettings, CombinePortfoliosWizardConfiguration } from './types';
import { CombineSettingsStep } from './CombineSettingsStep';
import {
  CombinePortfoliosWizardContext,
  CombinePortfoliosWizardProvider,
} from './logic/CombinePortfoliosWizardContext';
import { OwnershipStep } from './OwnershipStep';
import { TooltipBodyDirection } from 'venn-ui-kit';
import { TemplateStep } from './TemplateStep';
import { useNavigateToView } from './logic/useNavigateToView';
import { getDestinationFriendlyName, getDestinationShortName } from './logic/utils';

type CombinePortfoliosWizardSteps = 'SETTINGS' | 'OWNERSHIP' | 'TEMPLATE';

interface CombinePortfoliosWizardProps {
  onClose: () => void;
  configuration: CombinePortfoliosWizardConfiguration;
}

const shouldSkipOwnershipStep = (combineSettings: CombinePortfoliosDraftSettings) =>
  combineSettings.structureOption === 'SEPARATE';

export const CombinePortfoliosWizard = ({ onClose, configuration }: CombinePortfoliosWizardProps) => {
  return (
    <CombinePortfoliosWizardProvider configuration={configuration}>
      <CombinePortfoliosWizardInner onClose={onClose} configuration={configuration} />
    </CombinePortfoliosWizardProvider>
  );
};

const CombinePortfoliosWizardInner = ({ onClose, configuration }: CombinePortfoliosWizardProps) => {
  const { combineSettings, ownershipSettings, portfolios, portfoliosLoading, templateSettings } =
    useContext(CombinePortfoliosWizardContext);
  const onlyOnePortfolio = configuration.portfolioInformation.length === 1;
  const [currentStep, setCurrentStep] = useState<CombinePortfoliosWizardSteps>(
    onlyOnePortfolio ? 'TEMPLATE' : 'SETTINGS',
  );
  const navigateToView = useNavigateToView(onClose);
  const [isLoading, setIsLoading] = useState(false);

  const title = useMemo(() => {
    switch (currentStep) {
      case 'SETTINGS':
        return `Open in ${getDestinationFriendlyName(configuration.destination)}`;
      case 'OWNERSHIP':
        return 'Set portfolio ownership';
      case 'TEMPLATE':
        return 'Select a template';
      default:
        throw assertExhaustive(currentStep);
    }
  }, [currentStep, configuration.destination]);

  const subtitle = useMemo(() => {
    switch (currentStep) {
      case 'SETTINGS':
      case 'OWNERSHIP':
        return undefined;
      case 'TEMPLATE':
        return `Create a new ${getDestinationShortName(configuration.destination).toLowerCase()} with the selected portfolios added as subjects.`;
      default:
        throw assertExhaustive(currentStep);
    }
  }, [currentStep, configuration.destination]);

  const nextAction = useMemo((): { label: string; action: () => void; error: string | null } => {
    let error = null;
    switch (currentStep) {
      case 'SETTINGS':
        if (combineSettings.combinedPortfolioName.trim().length === 0) {
          error = 'Portfolio name cannot be empty';
        }
        return {
          label: 'NEXT',
          action: () => setCurrentStep(shouldSkipOwnershipStep(combineSettings) ? 'TEMPLATE' : 'OWNERSHIP'),
          error,
        };
      case 'OWNERSHIP':
        if (portfolios?.some((p) => !ownershipSettings[p.name])) {
          error = 'Each portfolio must be assigned an ownership';
        }
        return { label: 'NEXT', action: () => setCurrentStep('TEMPLATE'), error };
      case 'TEMPLATE':
        if (!templateSettings.templateId) {
          error = 'A template must be selected';
        }
        return {
          label: `OPEN ${getDestinationShortName(configuration.destination).toUpperCase()}`,
          action: () => {
            setIsLoading(true);
            navigateToView();
          },
          error,
        };
      default:
        throw assertExhaustive(currentStep);
    }
  }, [
    currentStep,
    combineSettings,
    portfolios,
    ownershipSettings,
    navigateToView,
    templateSettings.templateId,
    configuration.destination,
  ]);

  const cancelAction = useMemo((): { label: string; action: () => void } => {
    switch (currentStep) {
      case 'SETTINGS':
        return { label: 'CANCEL', action: onClose };
      case 'OWNERSHIP':
        return { label: '< BACK', action: () => setCurrentStep('SETTINGS') };
      case 'TEMPLATE':
        if (onlyOnePortfolio) {
          return { label: 'CANCEL', action: onClose };
        }
        return {
          label: '< BACK',
          action: () => setCurrentStep(shouldSkipOwnershipStep(combineSettings) ? 'SETTINGS' : 'OWNERSHIP'),
        };
      default:
        throw assertExhaustive(currentStep);
    }
  }, [currentStep, onClose, combineSettings, onlyOnePortfolio]);

  return (
    <SidePanelOverlay.Root
      isOpen
      side="right"
      handleClose={onClose}
      hideTopBackButton
      noPadding
      margin={0}
      width={1062}
    >
      <ConditionalOverlay
        contentClassName={cn('flex flex-col flex-1')}
        className={cn('flex flex-col flex-1')}
        center
        condition={isLoading}
      >
        <SidePanelOverlay.Header.Container paddingTop="default">
          <SidePanelOverlay.Header.Main title={title} subtitle={subtitle} />
        </SidePanelOverlay.Header.Container>
        <div className={cn('flex flex-1')}>
          <div className={cn('flex-1')}>
            <StepContent currentStep={currentStep} />
          </div>
          <div className={cn('flex-1')}>
            <PortfolioPreview />
          </div>
        </div>
        <SidePanelOverlay.Footer
          noMargin
          primaryLabel={nextAction.label}
          primaryDisabled={!!nextAction.error || portfoliosLoading}
          primaryTooltip={nextAction.error ?? undefined}
          tooltipBodyDirection={TooltipBodyDirection.Left}
          onPrimaryClick={nextAction.action}
          cancelLabel={cancelAction.label}
          onCancel={cancelAction.action}
        />
      </ConditionalOverlay>
    </SidePanelOverlay.Root>
  );
};

const StepContent = ({ currentStep }: { currentStep: CombinePortfoliosWizardSteps }) => {
  switch (currentStep) {
    case 'SETTINGS':
      return <CombineSettingsStep />;
    case 'OWNERSHIP':
      return <OwnershipStep />;
    case 'TEMPLATE':
      return <TemplateStep />;
    default:
      throw assertExhaustive(currentStep);
  }
};
